import React, { useEffect, useRef, useState } from "react";
import styles from "./style/HomeNavbar.module.css";

import logo from "../../../assets/frontendpage/home/MTCLogo.png";
import login from "../../../assets/frontendpage/home/loginicon.svg";
import profile from "../../../assets/frontendpage/home/tutorpicture/tutorimg3.png";

import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  RiUser3Line,
  RiUserAddLine,
  RiLogoutCircleLine,
  RiArrowDropDownLine,
  RiArrowDropUpLine,
  RiMenuLine,
  RiCloseLine,
} from "react-icons/ri";
import { TfiHeadphoneAlt } from "react-icons/tfi";
import { GiSettingsKnobs } from "react-icons/gi";

import Logout from "../../../utils/commoncomponent/Logout";
import Signupmodal from "../../../utils/commoncomponent/Signupmodal";
import { adminAPI } from "../../../utils/axiosInstance";
import { userDataRequest } from "../../../redux/actions/userdataAction";

/** Routes where we show FAQ, contact, and login (or create-account on /login). */
const PUBLIC_AUTH_EXTRAS = new Set([
  "/",
  "/home",
  "/search-tutor",
  "/signup-as-student",
  "/signup-as-tutor",
  "/signup-as-institution",
  "/contactus",
  "/faq-page",
  "/pricing",
  "/login",
  "/resetpassword",
  "/changepassword",
  "/createpassword",
  "/emailverification",
  "/create-profile-student",
  "/create-profile-tutor",
  "/create-profile-institution",
]);

/** Mobile slide-out nav: auth + FAQ + contact (desktop unchanged). */
const MOBILE_SIDE_NAV_PATHS = new Set([
  "/",
  "/home",
  "/search-tutor",
  "/login",
  "/resetpassword",
  "/signup-as-student",
  "/signup-as-tutor",
  "/signup-as-institution",
  "/contactus",
  "/faq-page",
]);

const isPublicTutorRoute = (pathname) =>
  pathname === "/search-tutor" || pathname.startsWith("/view-tutor/");

const Navbar = () => {
  const [isSticky, setSticky] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isNarrowViewport, setIsNarrowViewport] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [registerModalOpen, setRegisterModalOpen] = useState(false);

  const location = useLocation();
  const dispatch = useDispatch();

  const dropdownRef = useRef(null);

  const role = useSelector((state) => state.auth.role);
  const userId = useSelector((state) => state.auth.userId);
  const userdata = useSelector((state) => state.userdata.data);
  const normalizedRole = role?.toLowerCase?.() || "";

  const handleLogout = Logout();

  /* ---------- FETCH USER DATA ---------- */

  useEffect(() => {
    if (userId) {
      dispatch(userDataRequest(userId));
    }
  }, [dispatch, userId]);

  /* ---------- PROFILE IMAGE ---------- */

  const profileImageSrc = userdata?.documentDTO?.docName
    ? `${adminAPI.defaults.baseURL}/files?userId=${userId}&filename=${userdata.documentDTO.docName}&v=${userdata.documentDTO.documentId || 0}`
    : profile;

  /* ---------- DROPDOWN ---------- */
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };
  // const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleClickOutside = (event) => {
    if (!dropdownRef.current) return;
    if (!dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  /* ---------- STICKY NAVBAR ---------- */

  useEffect(() => {
    const handleScroll = () => setSticky(window.scrollY > 0);

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const update = () => setIsNarrowViewport(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [drawerOpen]);

  const showMobileAuthDrawer =
    !role &&
    isNarrowViewport &&
    (MOBILE_SIDE_NAV_PATHS.has(location.pathname) ||
      isPublicTutorRoute(location.pathname));

  const showPublicNavLinks = !role;

  const closeDrawer = () => setDrawerOpen(false);

  const openRegisterModal = () => {
    closeDrawer();
    setRegisterModalOpen(true);
  };

  const closeRegisterModal = () => setRegisterModalOpen(false);

  /* ---------- AUTH BUTTON ---------- */

  const renderAuthButton = () => {
    if (role) {
      const profileRoute =
        normalizedRole === "institution"
          ? `/${normalizedRole}/profile/view`
          : normalizedRole === "coordinator"
            ? "/coordinator/dashboard"
            : `/${normalizedRole}/profile`;
      const settingsRoute =
        normalizedRole === "coordinator"
          ? "/coordinator/dashboard"
          : `/${normalizedRole}/settings`;
      const contactRoute =
        normalizedRole === "coordinator"
          ? "/coordinator/demo-classes"
          : `/${normalizedRole}/contact`;

      return (
        <div className={styles.profileDropdown} ref={dropdownRef}>
          <button
            className={styles.profileDropbtn}
            onClick={(e) => {
              e.stopPropagation();
              toggleDropdown();
            }}
          >
            <img
              src={profileImageSrc}
              alt="Profile"
              className={styles.profilePicture}
            />

            <span className={styles.arrowIcon}>
              {isOpen ? <RiArrowDropUpLine /> : <RiArrowDropDownLine />}
            </span>
          </button>

          {isOpen && (
            <div className={styles.dropdownContent}>
              <div className={styles.dropdownHeader}>
                <img
                  src={profileImageSrc}
                  alt="Profile"
                  className={styles.dropdownProfilePicture}
                />
                <div>
                  <p>{userdata?.userName || "User Name"}</p>
                  <p style={{ color: "#7F60F9", fontSize: "16px" }}>
                    {userdata?.contactDetails?.emailId || "email"}
                  </p>
                </div>
              </div>

              <Link
                to={profileRoute}
              >
                <span>
                  <RiUser3Line />
                </span>
                Profile
              </Link>

              <Link to={settingsRoute}>
                <span>
                  <GiSettingsKnobs />
                </span>
                Settings
              </Link>

              <Link to={contactRoute}>
                <span>
                  <TfiHeadphoneAlt />
                </span>
                Contact Us
              </Link>

              <Link to="#" onClick={handleLogout}>
                <span>
                  <RiLogoutCircleLine />
                </span>
                Logout
              </Link>
            </div>
          )}
        </div>
      );
    }

    /* ---------- PUBLIC NAVBAR ---------- */

    if (
      PUBLIC_AUTH_EXTRAS.has(location.pathname) ||
      isPublicTutorRoute(location.pathname)
    ) {
      const onLoginPage = location.pathname === "/login";

      return (
        <>
          <Link to="/faq-page" className={styles.faqLink}>
            FAQ
          </Link>

          <Link to="/contactus" className={styles.faqLink}>
            Contact Us
          </Link>

          {!onLoginPage && (
            <button
              type="button"
              className={styles.loginBtn}
              onClick={openRegisterModal}
            >
              Register
            </button>
          )}

          {onLoginPage ? (
            <button
              type="button"
              className={styles.loginBtn}
              onClick={openRegisterModal}
            >
              Register
            </button>
          ) : (
            <Link to="/login" className={styles.signupBtn}>
              Login
            </Link>
          )}
        </>
      );
    }

    return null;
  };

  /* ---------- 30 MIn Auto logout ---------- */
  useEffect(() => {
    let logoutTimer;

    const resetTimer = () => {
      clearTimeout(logoutTimer);

      logoutTimer = setTimeout(
        () => {
          handleLogout();
        },
        45 * 60 * 1000,
      ); // 45 minutes
    };

    const events = ["mousemove", "keydown", "click", "scroll"];

    events.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    resetTimer(); // start timer

    return () => {
      clearTimeout(logoutTimer);
      events.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, []);
  /* ---------- JSX ---------- */

  const onLoginPage = location.pathname === "/login";

  return (
    <nav
      className={`${styles.navbar} ${isSticky ? styles.sticky : ""} ${
        showMobileAuthDrawer ? styles.navbarAuthMobile : ""
      }`}
    >
      {showMobileAuthDrawer ? (
        <>
          <div className={styles.mobileAuthTopBar}>
            <Link
              to="/home"
              className={styles.logoLink}
              aria-label="Book My Tuitions home"
              onClick={closeDrawer}
            >
              <img src={logo} alt="" className={styles.logo} />
            </Link>
            <button
              type="button"
              className={styles.menuButton}
              aria-label="Open navigation menu"
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen(true)}
            >
              <RiMenuLine size={26} />
            </button>
          </div>

          {drawerOpen && (
            <>
              <div
                className={styles.drawerScrim}
                aria-hidden
                onClick={closeDrawer}
              />
              <div
                className={styles.drawerPanel}
                role="dialog"
                aria-modal="true"
                aria-label="Main navigation"
              >
                <div className={styles.drawerHeader}>
                  <span className={styles.drawerTitle}>Menu</span>
                  <button
                    type="button"
                    className={styles.drawerClose}
                    aria-label="Close menu"
                    onClick={closeDrawer}
                  >
                    <RiCloseLine size={26} />
                  </button>
                </div>
                <div className={styles.drawerBody}>
                  <Link
                    to="/search-tutor"
                    className={styles.drawerLink}
                    onClick={closeDrawer}
                  >
                    Find Tutors
                  </Link>
                  <Link
                    to="/signup-as-tutor"
                    className={styles.drawerLink}
                    onClick={closeDrawer}
                  >
                    Become a Tutor
                  </Link>
                  <Link
                    to="/signup-as-institution"
                    className={styles.drawerLink}
                    onClick={closeDrawer}
                  >
                    Join as Institution
                  </Link>
                  <div className={styles.drawerDivider} />
                  <Link
                    to="/faq-page"
                    className={styles.drawerLink}
                    onClick={closeDrawer}
                  >
                    FAQ
                  </Link>
                  <Link
                    to="/contactus"
                    className={styles.drawerLink}
                    onClick={closeDrawer}
                  >
                    <TfiHeadphoneAlt className={styles.drawerLinkIcon} />
                    Contact support
                  </Link>
                  {onLoginPage ? (
                    <button
                      type="button"
                      className={`${styles.drawerLink} ${styles.drawerLinkPrimary}`}
                      onClick={openRegisterModal}
                    >
                      <RiUserAddLine size={22} className={styles.drawerLinkIconLight} />
                      Register
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className={styles.drawerLink}
                        onClick={openRegisterModal}
                      >
                        <RiUserAddLine size={22} className={styles.drawerLinkIcon} />
                        Register
                      </button>
                      <Link
                        to="/login"
                        className={`${styles.drawerLink} ${styles.drawerLinkPrimary}`}
                        onClick={closeDrawer}
                      >
                        <img
                          src={login}
                          alt=""
                          className={styles.drawerLoginIcon}
                        />
                        Login
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <div className={styles.leftSection}>
            <Link
              to="/home"
              className={styles.logoLink}
              aria-label="Book My Tuitions home"
            >
              <img src={logo} alt="" className={styles.logo} />
            </Link>
          </div>

          {showPublicNavLinks && (
            <nav className={styles.centerSection} aria-label="Main navigation">
              <Link
                to="/search-tutor"
                className={`${styles.navLink} ${
                  location.pathname === "/search-tutor" ? styles.active : ""
                }`}
              >
                Find Tutors
              </Link>
              <Link to="/signup-as-tutor" className={styles.navLink}>
                Become a Tutor
              </Link>
              <Link to="/signup-as-institution" className={styles.navLink}>
                Join as Institution
              </Link>
            </nav>
          )}

          <div className={styles.rightSection}>
            {renderAuthButton()}
          </div>
        </>
      )}
      <Signupmodal
        isOpen={registerModalOpen}
        onRequestClose={closeRegisterModal}
      />
    </nav>
  );
};

export default Navbar;
